import React from 'react';
import { Box, Typography, Tooltip, useTheme } from '@mui/material';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { config } from '../../../config';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import type { KnowledgePillItem } from '../stores/useCopilotStore';

const TYPE_LABEL: Record<string, string> = {
  metric: 'metric',
  business_rule: 'rule',
  term: 'term',
};

const INFO_TOOLTIP = `${config.brandName} automatically learns your preferences and saves them to your Knowledge Base. These definitions will be applied in future analyses.`;

interface KnowledgePillProps {
  items: KnowledgePillItem[];
}

export default function KnowledgePill({ items }: KnowledgePillProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (!items || items.length === 0) return null;

  const borderColor = isDark ? '#2d3748' : '#e2e8f0';
  const bgColor = isDark ? '#1a1f2e' : '#ffffff';

  return (
    <Box
      sx={{
        my: 0.75,
        width: '100%',
        borderRadius: '10px',
        border: '1px solid',
        borderColor,
        bgcolor: bgColor,
        overflow: 'hidden',
        transition: 'box-shadow 0.15s ease',
        '&:hover': {
          boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
        },
      }}
    >
      {/* Header row - mirrors "Auto-generated memory was updated ⓘ  Manage →" */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.9,
          borderBottom: '1px solid',
          borderColor,
          gap: 0.5,
        }}
      >
        <AutoAwesomeRounded sx={{ fontSize: 14, color: isDark ? '#818cf8' : '#6366f1', flexShrink: 0 }} />
        <Typography
          sx={{ fontSize: '0.8rem', fontWeight: 700, color: isDark ? '#e2e8f0' : '#111827', flex: 1, lineHeight: 1 }}
        >
          Knowledge base was updated
        </Typography>
        <Tooltip title={INFO_TOOLTIP} placement="top" arrow>
          <InfoOutlinedIcon
            sx={{ fontSize: 14, color: isDark ? '#64748b' : '#9ca3af', cursor: 'default', flexShrink: 0 }}
          />
        </Tooltip>
      </Box>

      {/* One row per captured item */}
      {items.map((item, i) => (
        <ItemRow
          key={i}
          item={item}
          isDark={isDark}
          borderColor={borderColor}
          isLast={i === items.length - 1}
        />
      ))}
    </Box>
  );
}

function ItemRow({
  item,
  isDark,
  borderColor,
  isLast,
}: {
  item: KnowledgePillItem;
  isDark: boolean;
  borderColor: string;
  isLast: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const typeLabel = TYPE_LABEL[item.node_type] || item.node_type;
  const hasDetail = !!(item.definition || item.sql);

  return (
    <Box sx={{ borderBottom: isLast ? 'none' : '1px solid', borderColor }}>
      {/* Item summary row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.7,
          gap: 1,
        }}
      >
        {/* Name */}
        <Typography
          sx={(t) => ({
            fontSize: '0.8125rem',
            color: t.tokens.text.secondary,
            flex: 1,
            lineHeight: 1.4,
          })}
        >
          Saved{' '}
          <Typography component="span" sx={(t) => ({ fontWeight: 600, color: t.tokens.text.primary, fontSize: '0.8125rem' })}>
            {item.name}
          </Typography>
          {' '}
          <Typography component="span" sx={(t) => ({ fontSize: '0.75rem', color: t.tokens.text.tertiary })}>
            ({typeLabel})
          </Typography>
          {' '}to knowledge base
        </Typography>

        {/* Expand / Collapse toggle */}
        {hasDetail && (
          <Box
            onClick={() => setOpen((v) => !v)}
            sx={{
              cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.25,
              color: isDark ? '#64748b' : '#9ca3af',
              borderRadius: 1,
              px: 0.5,
              py: 0.15,
              '&:hover': { color: isDark ? '#818cf8' : '#6366f1', bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' },
              transition: 'all 0.12s ease',
            }}
          >
            {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          </Box>
        )}
      </Box>

      {/* Expanded detail */}
      {open && hasDetail && (
        <Box
          sx={{
            mx: 1.5,
            mb: 0.75,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#f9fafb',
            border: '1px solid',
            borderColor,
          }}
        >
          {item.definition && (
            <Typography sx={{ fontSize: '0.75rem', color: isDark ? '#cbd5e1' : '#374151', lineHeight: 1.6 }}>
              {item.definition}
            </Typography>
          )}
          {item.sql && (
            <Box
              sx={(t) => ({
                mt: item.definition ? 0.5 : 0,
                px: 0.75,
                py: 0.4,
                borderRadius: `${t.ds.radius.xs}px`,
                bgcolor: t.tokens.surface.canvas,
                fontFamily: t.typography.fontFamilyMono,
                fontSize: '0.75rem',
                color: t.tokens.brand.primary,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              })}
            >
              {item.sql}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
